import React, { useCallback, useEffect, useRef, useState } from 'react';
import GrabMaps, { Layer, Source, ZoomControl } from '@geo/react-grabmaps-gl';
import * as turf from '@turf/turf';
import debounce from 'lodash/debounce';

const Map = GrabMaps({
  showLogo: false,
  attributionControl: false,
});

interface GrabMapProps {
  geoJson?: any;
  style?: React.CSSProperties;
  panelSize?: number;
}

const convertToGeofenceGeoJSON = (data) => {
  const features = {
    type: 'FeatureCollection',
    features: [data],
  };

  return {
    type: 'geojson',
    data: features,
  };
};

const GrabMap: React.FC<GrabMapProps> = ({ geoJson, style, panelSize }) => {
  const map = useRef(null);
  const [geofence, setGeofence] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const handleMapOnLoaded = (_, mapInstance) => {
    setMapLoaded(true);
    map.current = mapInstance?.target;
    map.current?.resize?.();
  };

  const updateGeofenceAndFitBounds = (responseGeoJson) => {
    const geojson = convertToGeofenceGeoJSON(responseGeoJson);
    setGeofence(geojson);
    handleMapFitBounds(responseGeoJson);
  };

  const handleMapFitBounds = (feature) => {
    try {
      const bbox = turf.bbox(feature);
      map.current.fitBounds(bbox, {
        padding: 10,
      });
    } catch (err) {
      console.error('calculate map center error:', err);
    }
  };

  useEffect(() => {
    if (!mapLoaded) return;
    if (geoJson) {
      updateGeofenceAndFitBounds(geoJson);
    }
  }, [mapLoaded, geoJson]);

  const debounceResize = debounce(() => {
    map.current?.resize?.();
  }, 300);

  const handlePanelResize = useCallback(debounceResize, []);

  useEffect(() => {
    handlePanelResize();
  }, [panelSize]);

  return (
    <Map
      style={{
        width: '100%',
        height: '100%',
        borderRadius: 12,
        border: '1px solid #DBDBDB',
        ...style,
      }}
      onStyleLoad={handleMapOnLoaded}
    >
      <ZoomControl position="bottom-right" style={{ bottom: 16, right: 16 }} />
      {geofence && (
        <>
          <Source geoJsonSource={geofence || {}} id="geojson_source" />
          <Layer
            id="geojson_source_line"
            type="line"
            sourceId="geojson_source"
            layout={{
              visibility: 'visible',
            }}
            paint={{
              'line-color': '#00b14f',
              'line-width': 3,
            }}
          />
          <Layer
            id="geojson_source_layer"
            type="fill"
            sourceId="geojson_source"
            layout={{
              visibility: 'visible',
            }}
            paint={{
              'fill-color': '#00b14f',
              'fill-opacity': 0.5,
            }}
          />
        </>
      )}
    </Map>
  );
};

export default GrabMap;
